import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Task, TaskStatus, TaskPriority } from '../types/task'
import { taskService } from '../services/taskService'
import { userService } from '../services/userService'
import { useAuth } from '../contexts/AuthContext'
import { TaskItem } from './TaskItem'
import { TaskForm } from './TaskForm'
import { formatUserName } from '../utils/name'
import './TaskList.css'

export function TaskList() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showCreateForm, setShowCreateForm] = useState(false)
  // Default to "Assigned to Me" if user is available, otherwise "all"
  // Only set initial default - don't force change if user manually selects "All Tasks"
  const [filter, setFilter] = useState<'all' | 'assigned-to-me' | 'assigned-by-me' | 'pending' | 'completed'>(
    user?.id ? 'assigned-to-me' : 'all'
  )
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'all'>('all')
  
  // Update filter to "Assigned to Me" when user becomes available, but only if filter hasn't been manually set
  // Use a ref to track if user has manually selected a filter
  const [hasManualFilter, setHasManualFilter] = useState(false)
  
  useEffect(() => {
    // Only auto-change if user becomes available AND filter is 'all' AND user hasn't manually selected a filter
    if (user?.id && filter === 'all' && !hasManualFilter) {
      setFilter('assigned-to-me')
    }
  }, [user?.id, filter, hasManualFilter])
  
  // Track when user manually changes filter
  const handleFilterChange = (newFilter: 'all' | 'assigned-to-me' | 'assigned-by-me' | 'pending' | 'completed') => {
    setHasManualFilter(true)
    setFilter(newFilter)
  }

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => taskService.getAll(),
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const getUserName = (userId?: string) => {
    if (!userId) return 'Unassigned'
    const user = users.find((u) => u.id === userId)
    return user ? formatUserName(user as any) : userId
  }

  const createMutation = useMutation({
    mutationFn: taskService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setShowCreateForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Task> }) =>
      taskService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks'] })
      
      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<Task[]>(['tasks'])
      
      // Optimistically update the task in the list
      if (previousTasks) {
        const now = new Date().toISOString()
        queryClient.setQueryData<Task[]>(['tasks'], (old) =>
          old?.map((task) => {
            if (task.id === id) {
              const updated = { ...task, ...data, updatedAt: now }
              // Set timestamps based on status changes
              if (data.status === 'in-progress' && task.status !== 'in-progress') {
                updated.startedAt = now
              }
              if ((data.status === 'completed' || data.status === 'resolved') && task.status !== 'completed' && task.status !== 'resolved') {
                updated.completedAt = now
              }
              if (data.status === 'resolved' && task.status !== 'resolved') {
                updated.resolvedAt = now
              }
              return updated
            }
            return task
          })
        )
      }
      
      return { previousTasks }
    },
    onError: (_err, _variables, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks'], context.previousTasks)
      }
    },
    onSuccess: () => {
      // Always refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: taskService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'assigned-to-me' && task.assignedTo !== user?.id) return false
    if (filter === 'assigned-by-me' && task.assignedBy !== user?.id) return false
    if (filter === 'pending' && task.status !== 'pending') return false
    if (filter === 'completed' && task.status !== 'completed') return false
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false
    return true
  })

  const handleCreate = (formData: any) => {
    createMutation.mutate({
      ...formData,
      assignedBy: user?.id,
    })
  }

  const handleUpdate = (id: string, updates: Partial<Task>) => {
    updateMutation.mutate({ id, data: updates })
  }

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleStatusChange = (id: string, status: TaskStatus) => {
    const updates: Partial<Task> = { status }
    const now = new Date().toISOString()
    if (status === 'completed' || status === 'resolved') {
      updates.completedAt = now
    }
    if (status === 'in-progress') {
      updates.startedAt = now
    }
    if (status === 'resolved') {
      updates.resolvedAt = now
    }
    handleUpdate(id, updates)
  }

  if (isLoading) {
    return <div className="task-list-loading">Loading tasks...</div>
  }

  return (
    <div className="task-list">
      <div className="task-list-header">
        <button
          className="btn-create-task"
          onClick={() => setShowCreateForm(true)}
        >
          + New Task
        </button>
      </div>

      <div className="task-list-filters">
        <select
          className="task-filter"
          value={filter}
          onChange={(e) => handleFilterChange(e.target.value as any)}
        >
          <option value="all">All Tasks</option>
          <option value="assigned-to-me">Assigned to Me</option>
          <option value="assigned-by-me">Assigned by Me</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
        </select>

        <select
          className="task-filter"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | 'all')}
        >
          <option value="all">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {showCreateForm && (
        <div className="task-create-form-container">
          <TaskForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      <div className="task-list-items">
        {filteredTasks.length === 0 ? (
          <div className="task-list-empty">No tasks found</div>
        ) : (
          filteredTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
              getUserName={getUserName}
            />
          ))
        )}
      </div>
    </div>
  )
}














